/**
 * The Terms of Service and Privacy Policy modals.
 *
 * Login.cshtml and Register.cshtml each carried a full copy of both, rendered
 * into _LoginLayout's `Modals` section. They are one component here; the body
 * text is transcribed verbatim, because it is a legal notice referencing
 * UU No. 27 Tahun 2022 and must not be paraphrased.
 *
 * They are plain Bootstrap modals driven by `data-bs-toggle`/`data-bs-dismiss`,
 * which bootstrap.min.js binds at the document level — no React state needed.
 */
export function LegalModals(): JSX.Element {
  return (
    <>
      <div
        className="modal fade"
        id="modalToS"
        tabIndex={-1}
        aria-labelledby="modalToSLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-scrollable modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-semibold" id="modalToSLabel">
                Terms of Service
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div
              className="modal-body"
              style={{ fontSize: 14, color: '#374151', lineHeight: 1.7 }}
            >
              <h6 className="fw-semibold mt-3">1. Acceptance of Terms</h6>
              <p>
                By registering and using the TBG E-Career platform, you agree to be bound by these
                Terms of Service. If you do not agree, please do not use the platform.
              </p>
              <h6 className="fw-semibold mt-3">2. Eligibility</h6>
              <p>
                You must be at least 18 years of age to register. By creating an account, you
                confirm that the information you provide is accurate, current, and complete.
              </p>
              <h6 className="fw-semibold mt-3">3. Account Responsibility</h6>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials.
                Any activity that occurs under your account is your responsibility. Please notify us
                immediately of any unauthorized use.
              </p>
              <h6 className="fw-semibold mt-3">4. Use of the Platform</h6>
              <p>
                The platform is provided solely for legitimate job application and recruitment
                purposes within PT Tower Bersama Group. You may not use the platform to submit false
                information, impersonate others, or engage in any unlawful activity.
              </p>
              <h6 className="fw-semibold mt-3">5. Submitted Content</h6>
              <p>
                By submitting your profile, CV, and other application materials, you grant PT Tower
                Bersama Group a non-exclusive license to review and process your data for recruitment
                purposes.
              </p>
              <h6 className="fw-semibold mt-3">6. Termination</h6>
              <p>
                We reserve the right to suspend or terminate your account at any time if you are
                found to be in violation of these terms.
              </p>
              <h6 className="fw-semibold mt-3">7. Changes to Terms</h6>
              <p>
                We may update these terms from time to time. Continued use of the platform after
                changes are posted constitutes your acceptance of the revised terms.
              </p>
              <h6 className="fw-semibold mt-3">8. Contact</h6>
              <p>
                For questions regarding these Terms, please contact us at{' '}
                <strong>Recruitment.tbg@tower-bersama.com</strong>.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-primary btn-sm" data-bs-dismiss="modal">
                I Understand
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalPrivacy"
        tabIndex={-1}
        aria-labelledby="modalPrivacyLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-scrollable modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-semibold" id="modalPrivacyLabel">
                Privacy Policy
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div
              className="modal-body"
              style={{ fontSize: 14, color: '#374151', lineHeight: 1.7 }}
            >
              <h6 className="fw-semibold mt-3">1. Data Controller</h6>
              <p>
                PT Tower Bersama Infrastructure, Tbk. (&quot;TBG&quot;) is the data controller
                responsible for processing your personal data collected through this platform.
              </p>
              <h6 className="fw-semibold mt-3">2. Data We Collect</h6>
              <p>
                We collect personal data you voluntarily provide during registration and
                application, including but not limited to:
              </p>
              <ul>
                <li>Full name, email address, phone number</li>
                <li>Educational background and work experience</li>
                <li>Curriculum vitae and supporting documents</li>
                <li>Device information and IP address (for security purposes)</li>
              </ul>
              <h6 className="fw-semibold mt-3">3. Purpose of Processing</h6>
              <p>
                Your data is collected and processed exclusively for evaluating your application,
                communicating with you regarding recruitment progress, and maintaining the security
                and integrity of the platform.
              </p>
              <h6 className="fw-semibold mt-3">4. Data Sharing</h6>
              <p>
                We do not sell or share your personal data with third parties outside of TBG Group
                entities involved in the recruitment process, except as required by applicable law.
              </p>
              <h6 className="fw-semibold mt-3">5. Data Retention</h6>
              <p>
                Your personal data will be retained for the duration necessary to fulfill the
                recruitment process, or as required by applicable Indonesian law (UU No. 27 Tahun
                2022 tentang Perlindungan Data Pribadi).
              </p>
              <h6 className="fw-semibold mt-3">6. Your Rights</h6>
              <p>
                You have the right to access, correct, or request deletion of your personal data. To
                exercise these rights, please contact{' '}
                <strong>Recruitment.tbg@tower-bersama.com</strong>.
              </p>
              <h6 className="fw-semibold mt-3">7. Security</h6>
              <p>
                We implement appropriate technical and organizational measures to protect your data
                against unauthorized access, disclosure, or destruction.
              </p>
              <h6 className="fw-semibold mt-3">8. Contact</h6>
              <p>
                For privacy-related inquiries, contact our Data Protection Officer at{' '}
                <strong>Recruitment.tbg@tower-bersama.com</strong>.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-primary btn-sm" data-bs-dismiss="modal">
                I Understand
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
